import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell as MuiTableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Chip,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { tokens } from '../../theme';
import TableCell from './TableCell';
import {
  defaultTableConfig,
  addColumn,
  removeColumn,
  addRow,
  removeRow,
  updateCell,
  updateColumnHeader,
  updateColumnType,
  extractAnswerFromConfig,
} from './tableUtils';

/**
 * TableAdmin Component
 * Admin interface for configuring table-based homework questions.
 * Allows setting columns, rows, prefilled values, and grading options.
 */
const TableAdmin = ({
  config = defaultTableConfig,
  onConfigChange,
  correctAnswer = { rows: [] },
  onAnswerChange,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Local state for grading options
  const [gradingMode, setGradingMode] = useState(config.grading?.mode || 'exact');
  const [tolerance, setTolerance] = useState(config.grading?.tolerance || 0.01);
  const [rowOrderFlexible, setRowOrderFlexible] = useState(config.grading?.rowOrderFlexible || false);
  const [caseSensitive, setCaseSensitive] = useState(config.grading?.caseSensitive || false);

  // Sync grading options with config
  useEffect(() => {
    setGradingMode(config.grading?.mode || 'exact');
    setTolerance(config.grading?.tolerance || 0.01);
    setRowOrderFlexible(config.grading?.rowOrderFlexible || false);
    setCaseSensitive(config.grading?.caseSensitive || false);
  }, [config.grading]);

  // Sync external `correctAnswer` writes back into config.cells.value.
  // Cells render from config (lines 302/387), so a parent that updates
  // correctAnswer (e.g. from extraction or a re-solve response) needs its
  // values merged into config to actually appear in the editor. The diff
  // guard makes the effect idempotent: it only fires onConfigChange when
  // values genuinely differ, so editing a cell (which already updates both
  // config and correctAnswer via handleCellValueChange → onAnswerChange)
  // does not trigger a re-fire.
  useEffect(() => {
    if (!correctAnswer?.rows || !Array.isArray(correctAnswer.rows)) return;
    let anyChanged = false;
    const newRows = config.rows.map((row, i) => {
      const ansRow = correctAnswer.rows[i];
      if (!ansRow?.cells) return row;
      let rowChanged = false;
      const newCells = { ...row.cells };
      Object.entries(ansRow.cells).forEach(([colId, val]) => {
        if (newCells[colId] && newCells[colId].value !== val) {
          newCells[colId] = { ...newCells[colId], value: val };
          rowChanged = true;
        }
      });
      if (rowChanged) {
        anyChanged = true;
        return { ...row, cells: newCells };
      }
      return row;
    });
    if (anyChanged) onConfigChange?.({ ...config, rows: newRows });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [correctAnswer]);

  // Update config when grading options change
  const updateGrading = (updates) => {
    const newGrading = {
      ...config.grading,
      ...updates,
    };
    onConfigChange?.({
      ...config,
      grading: newGrading,
    });
  };

  // Column handlers
  const handleAddColumn = () => {
    const newConfig = addColumn(config, '', 'math');
    onConfigChange?.(newConfig);
    onAnswerChange?.(extractAnswerFromConfig(newConfig));
  };

  const handleRemoveColumn = (columnId) => {
    if (config.columns.length <= 1) return; // Keep at least one column
    const newConfig = removeColumn(config, columnId);
    onConfigChange?.(newConfig);
    onAnswerChange?.(extractAnswerFromConfig(newConfig));
  };

  const handleColumnHeaderChange = (columnId, header) => {
    const newConfig = updateColumnHeader(config, columnId, header);
    onConfigChange?.(newConfig);
  };

  const handleColumnTypeChange = (columnId, type) => {
    const newConfig = updateColumnType(config, columnId, type);
    onConfigChange?.(newConfig);
  };

  // Row handlers
  const handleAddRow = () => {
    const newConfig = addRow(config);
    onConfigChange?.(newConfig);
    onAnswerChange?.(extractAnswerFromConfig(newConfig));
  };

  const handleRemoveRow = (rowId) => {
    if (config.rows.length <= 1) return; // Keep at least one row
    const newConfig = removeRow(config, rowId);
    onConfigChange?.(newConfig);
    onAnswerChange?.(extractAnswerFromConfig(newConfig));
  };

  // Cell handlers
  const handleCellValueChange = (rowId, colId, value) => {
    const newConfig = updateCell(config, rowId, colId, { value });
    onConfigChange?.(newConfig);
    onAnswerChange?.(extractAnswerFromConfig(newConfig));
  };

  const handleCellPrefilledToggle = (rowId, colId) => {
    const currentCell = config.rows.find(r => r.id === rowId)?.cells[colId];
    const newConfig = updateCell(config, rowId, colId, {
      prefilled: !currentCell?.prefilled
    });
    onConfigChange?.(newConfig);
  };

  const getColumnType = (columnId) => {
    return config.columns.find(c => c.id === columnId)?.type || 'math';
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Grading Options Section */}
      <Paper sx={{ p: 2, mb: 2, backgroundColor: colors.primary[600] }}>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
          Grading Options
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Grading Mode</InputLabel>
            <Select
              value={gradingMode}
              label="Grading Mode"
              onChange={(e) => {
                setGradingMode(e.target.value);
                updateGrading({ mode: e.target.value });
              }}
            >
              <MenuItem value="exact">Exact Match</MenuItem>
              <MenuItem value="tolerance">Tolerance</MenuItem>
            </Select>
          </FormControl>

          {gradingMode === 'tolerance' && (
            <TextField
              label="Tolerance"
              type="number"
              size="small"
              value={tolerance}
              onChange={(e) => {
                const val = parseFloat(e.target.value) || 0.01;
                setTolerance(val);
                updateGrading({ tolerance: val });
              }}
              inputProps={{ step: 0.01, min: 0 }}
              sx={{ width: 100 }}
            />
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={rowOrderFlexible}
                onChange={(e) => {
                  setRowOrderFlexible(e.target.checked);
                  updateGrading({ rowOrderFlexible: e.target.checked });
                }}
                size="small"
              />
            }
            label="Row Order Flexible"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={caseSensitive}
                onChange={(e) => {
                  setCaseSensitive(e.target.checked);
                  updateGrading({ caseSensitive: e.target.checked });
                }}
                size="small"
              />
            }
            label="Case Sensitive (text)"
          />
        </Box>
      </Paper>

      {/* Table Structure Section */}
      <Paper sx={{ p: 2, backgroundColor: colors.primary[600] }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            Table Structure
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddColumn}
              variant="outlined"
            >
              Add Column
            </Button>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddRow}
              variant="outlined"
            >
              Add Row
            </Button>
          </Box>
        </Box>

        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
          Click the lock icon to toggle prefilled cells (students cannot edit prefilled cells).
          Enter the correct answer for each cell.
        </Typography>

        <TableContainer>
          <Table size="small" sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <MuiTableCell sx={{ width: 50 }}>#</MuiTableCell>
                {config.columns.map((col, colIndex) => (
                  <MuiTableCell key={col.id} sx={{ minWidth: 150 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                          size="small"
                          placeholder={`Col ${colIndex + 1}`}
                          value={col.header}
                          onChange={(e) => handleColumnHeaderChange(col.id, e.target.value)}
                          sx={{ flexGrow: 1 }}
                        />
                        {config.columns.length > 1 && (
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveColumn(col.id)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                      <Select
                        size="small"
                        value={col.type}
                        onChange={(e) => handleColumnTypeChange(col.id, e.target.value)}
                        sx={{ fontSize: '0.75rem' }}
                      >
                        <MenuItem value="math">Math</MenuItem>
                        <MenuItem value="text">Text</MenuItem>
                      </Select>
                    </Box>
                  </MuiTableCell>
                ))}
                <MuiTableCell sx={{ width: 60 }}>Actions</MuiTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {config.rows.map((row, rowIndex) => (
                <TableRow key={row.id}>
                  <MuiTableCell>{rowIndex + 1}</MuiTableCell>
                  {config.columns.map((col) => {
                    const cell = row.cells[col.id] || { value: '', prefilled: false };
                    return (
                      <MuiTableCell key={col.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Tooltip title={cell.prefilled ? "Prefilled (student cannot edit)" : "Editable by student"}>
                            <IconButton
                              size="small"
                              onClick={() => handleCellPrefilledToggle(row.id, col.id)}
                              color={cell.prefilled ? "primary" : "default"}
                            >
                              {cell.prefilled ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          <TableCell
                            value={cell.value}
                            onChange={(value) => handleCellValueChange(row.id, col.id, value)}
                            type={getColumnType(col.id)}
                            size="small"
                            sx={{ flexGrow: 1 }}
                          />
                        </Box>
                      </MuiTableCell>
                    );
                  })}
                  <MuiTableCell>
                    {config.rows.length > 1 && (
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveRow(row.id)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </MuiTableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Legend */}
        <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
          <Chip
            icon={<LockIcon fontSize="small" />}
            label="Prefilled (given to student)"
            size="small"
            variant="outlined"
            color="primary"
          />
          <Chip
            icon={<LockOpenIcon fontSize="small" />}
            label="Editable (student fills in)"
            size="small"
            variant="outlined"
          />
        </Box>
      </Paper>

      {/* Preview Section */}
      <Paper sx={{ p: 2, mt: 2, backgroundColor: colors.primary[600] }}>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
          Student Preview
        </Typography>
        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
          This is how students will see the table. Prefilled cells appear as read-only.
        </Typography>

        <TableContainer>
          <Table size="small" sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                {config.columns.map((col) => (
                  <MuiTableCell key={col.id} sx={{ fontWeight: 'bold', minWidth: 100 }}>
                    {col.type === 'math' ? (
                      <TableCell value={col.header} type="math" readOnly size="small" />
                    ) : (
                      col.header || '-'
                    )}
                  </MuiTableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {config.rows.map((row) => (
                <TableRow key={row.id}>
                  {config.columns.map((col) => {
                    const cell = row.cells[col.id] || { value: '', prefilled: false };
                    return (
                      <MuiTableCell
                        key={col.id}
                        sx={{
                          backgroundColor: cell.prefilled
                            ? theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'
                            : 'transparent',
                        }}
                      >
                        {cell.prefilled ? (
                          <TableCell
                            value={cell.value}
                            type={getColumnType(col.id)}
                            readOnly
                            size="small"
                          />
                        ) : (
                          <Box
                            sx={{
                              border: '1px dashed',
                              borderColor: colors.grey[500],
                              borderRadius: 1,
                              minHeight: 32,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: colors.grey[500],
                              fontSize: '0.75rem',
                            }}
                          >
                            Student input
                          </Box>
                        )}
                      </MuiTableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default TableAdmin;
